import dayjs from "dayjs"; // date manipulation library
import { observer } from "mobx-react"; // MobX observer for reactivity
import moment from "moment"; // date formatting library
import { useEffect, useState } from "react"; // React hooks
import { Modal, Tab, Tabs } from "react-bootstrap"; // Bootstrap modal and tabs
import toast from "react-hot-toast"; // toast notifications
import { useStore } from "../../../../contexts/store-provider"; // custom store context
import { IOption } from "../../../../models/ICommon"; // option interface
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc"; // delete popup HOC
import { formatMessage } from "../../../../translations/format-message"; // i18n formatter
import AddBreakDetailsFormComponent from "../form-component/add-break-details-form-component"; // break details form

interface IProps {
  id: number; // break id
  day: any; // day info
  staffId: number; // staff id
  staffList: IOption[]; // staff options
  onModalClosed: () => void; // close modal callback
  deletePopupToggleHandler: () => void; // delete popup toggle callback
  startTime: string; // break start time
  storeId: number; // store id
  viewOnly: boolean; // view-only flag
}

const AddBreakDetailsComponent = (props: IProps) => {
  const { reservationSchedulerStore } = useStore(); // get store
  const {
    GetStaffBreakByIdService, // fetch break by id
    staffBreakDetails, // break details state
    getStaffBreakByIdState, // fetch state
    addStaffBreakState, // add state
    updateDraggableBreakState, // update state
    UpdateBreakDraggableService, // update service
    AddStaffBreakService, // add service
    resetGetStaffBreakByIdState, // reset fetch state
    DeleteStaffBreaksService, // delete service
    deleteStaffBreakByIdState, // delete state
    resetDeleteStaffBreakState, // reset delete state
  } = reservationSchedulerStore;
  const [activeTab, setActiveTab] = useState<any>("0"); // active tab state

  // set initial form values
  let initialValues =
    staffBreakDetails.Id > 0
      ? staffBreakDetails // use fetched details
      : {
          ...staffBreakDetails,
          Title: activeTab === "0" ? formatMessage("break") : "",
          StaffId: props.staffId, // set staff id
          StartTime: moment(
            `${localStorage.getItem("currentDate")}T${props.startTime}`
          ),
          EndTime: moment(
            `${localStorage.getItem("currentDate")}T${props.startTime}`
          ).add(15, "minute"), // default 15 min break
        };

  useEffect(() => {
    if (props.id > 0) {
      GetStaffBreakByIdService(props.id); // fetch break details if editing
    }
  }, []);

  useEffect(() => {
    if (staffBreakDetails.Id > 0) {
      setActiveTab(staffBreakDetails.BreakType?.toString()); // set tab from details
    }
  }, [staffBreakDetails]);

  useEffect(() => {
    if (addStaffBreakState.success) {
      toast.success(formatMessage("added_success")); // show add success
      onModalClosed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addStaffBreakState.success]);

  useEffect(() => {
    if (updateDraggableBreakState.success) {
      toast.success(formatMessage("updated_success")); // show update success
      onModalClosed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDraggableBreakState.success]);

  useEffect(() => {
    if (deleteStaffBreakByIdState.success) {
      toast.success(formatMessage("deleted_success")); // show delete success
      onDeleteModalClosed();
    }
  }, [deleteStaffBreakByIdState.success]);

  // handle form submit
  const onSubmitForm = (values: any) => {
    let addStaffBreak = {};
    if(props.id < 0){
      addStaffBreak = {
        Title: values.Title,
        Date: props.day.props["data-role"] == "weekly" ? props.day?.props['data-index'] : moment(localStorage.getItem("currentDate"))?.format("YYYY-MM-DD"),
        StartTime: `${values.StartTime}:00`,
        EndTime: `${values.EndTime}:00`,
        Remarks: values.Remarks,
        StaffId: Number(values?.StaffId),
        StoreId: props.storeId,
        BreakType: Number(activeTab),
      };
    }
    
    let updateStaffBreak = {
      Id: Number(values?.Id),
      Title: values.Title,
      Date: moment(values?.Date)?.format("YYYY-MM-DD"),
      StartTime: `${values.StartTime}:00`,
      EndTime: `${values.EndTime}:00`,
      Remarks: values.Remarks,
      StaffId: Number(values?.StaffId),
      BreakType:
        Number(values?.BreakType) !== Number(activeTab)
          ? Number(activeTab)
          : Number(values?.BreakType),
    };

    if (props.id > 0) {
      UpdateBreakDraggableService(updateStaffBreak); // update break
    } else {
      AddStaffBreakService(addStaffBreak); // add break
    }
  };

  // close modal handler
  const onModalClosed = () => {
    resetGetStaffBreakByIdState();
    props.onModalClosed();
  };

  // close delete modal handler
  const onDeleteModalClosed = () => {
    resetGetStaffBreakByIdState();
    props.deletePopupToggleHandler();
  };

  // delete break handler
  const deleteStaffBreakHandler = () => {
    DeleteStaffBreaksService(props.id);
  };

  return (
    <Modal show={true} centered size="sm">
      {/* modal header */}
      <Modal.Header className="pb-0">
        <Modal.Title>
          <button onClick={onModalClosed} className="Crossicons">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
        {/* tabs for break type */}
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
          id="reservation-tabs"
          className="border-0"
        >
          <Tab eventKey="0" title={formatMessage("break")} tabClassName="px-3">
            {/* break tab */}
          </Tab>
          <Tab
            eventKey="1"
            title={formatMessage("other_business")}
            tabClassName="px-3"
          >
            {/* other business tab */}
          </Tab>
        </Tabs>
      </Modal.Header>
      {/* break details form */}
      <AddBreakDetailsFormComponent
        id={props.id}
        initialValues={initialValues}
        day={props.day}
        staffId={props.staffId}
        staffList={[
          {
            id: -1,
            value: "please_select",
            disabled: true,
            data: undefined,
          },
          ...props.staffList,
        ]}
        startTime={props.startTime}
        onModalClosed={onModalClosed}
        handleDeleteSubmitHandler={deleteStaffBreakHandler}
        submitHandler={onSubmitForm}
        isLoading={
          addStaffBreakState.inProgress ||
          updateDraggableBreakState.inProgress ||
          getStaffBreakByIdState.inProgress ||
          deleteStaffBreakByIdState.inProgress
        }
        activeTabId={activeTab}
        viewOnly={props.viewOnly}
      />
    </Modal>
  );
};

export default DeletePopupHOC(observer(AddBreakDetailsComponent)); // export with observer and delete popup
